/**
 * useRequestPriorityThresholds
 *
 * Returns the configurable day-thresholds that map a resource request's
 * requested start date to a priority level (critical / high / medium / low).
 *
 * Thresholds are stored in AppSettings.requestPriorityThresholds and fall
 * back to sensible defaults when not configured.
 */

import { useMemo } from 'react';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { useScheduler } from '@/contexts/SchedulerContext';
import type { RequestPriority } from '@/types/resourceRequest';

export interface RequestPriorityThresholds {
  criticalDays: number; // ≤ N days  → critical
  highDays:     number; // ≤ N days  → high
  mediumDays:   number; // ≤ N days  → medium
  // > mediumDays → low
}

export type RequestPriorityThresholdKey = keyof RequestPriorityThresholds;
export type RequestPriorityThresholdErrors = Partial<Record<RequestPriorityThresholdKey, string>>;

export const DEFAULT_PRIORITY_THRESHOLDS: RequestPriorityThresholds = {
  criticalDays: 7,
  highDays:     14,
  mediumDays:   30,
};

export function normalizeRequestPriorityThresholds(
  thresholds: RequestPriorityThresholds,
): RequestPriorityThresholds {
  const criticalDays = Math.max(1, Math.trunc(thresholds.criticalDays));
  const highDays = Math.max(criticalDays + 1, Math.trunc(thresholds.highDays));
  const mediumDays = Math.max(highDays + 1, Math.trunc(thresholds.mediumDays));

  return { criticalDays, highDays, mediumDays };
}

export function validateRequestPriorityThresholds(
  thresholds: RequestPriorityThresholds,
): RequestPriorityThresholdErrors {
  const errors: RequestPriorityThresholdErrors = {};

  if (thresholds.criticalDays < 1) {
    errors.criticalDays = 'Critical threshold must be at least 1 day.';
  }

  if (thresholds.highDays <= thresholds.criticalDays) {
    errors.highDays = 'High threshold must be greater than Critical threshold.';
  }

  if (thresholds.mediumDays <= thresholds.highDays) {
    errors.mediumDays = 'Medium threshold must be greater than High threshold.';
  }

  return errors;
}

export function updateRequestPriorityThreshold(
  thresholds: RequestPriorityThresholds,
  field: RequestPriorityThresholdKey,
  value: number,
): RequestPriorityThresholds {
  return {
    ...thresholds,
    [field]: Math.trunc(value),
  };
}

export function useRequestPriorityThresholds(): RequestPriorityThresholds {
  const { settings } = useScheduler();

  return useMemo<RequestPriorityThresholds>(() => normalizeRequestPriorityThresholds({
    criticalDays: settings?.requestPriorityThresholds?.criticalDays ?? DEFAULT_PRIORITY_THRESHOLDS.criticalDays,
    highDays:     settings?.requestPriorityThresholds?.highDays     ?? DEFAULT_PRIORITY_THRESHOLDS.highDays,
    mediumDays:   settings?.requestPriorityThresholds?.mediumDays   ?? DEFAULT_PRIORITY_THRESHOLDS.mediumDays,
  }), [settings?.requestPriorityThresholds]);
}

/**
 * Pure function — compute priority from a start date and a threshold config.
 * "Days until start" is measured from today (calendar days, not business days).
 * Negative values (already past the start date) always → critical.
 */
export function computeRequestPriority(
  requestedStartDate: string,
  thresholds: RequestPriorityThresholds,
): RequestPriority {
  const days = differenceInCalendarDays(parseISO(requestedStartDate), new Date());
  if (days <= thresholds.criticalDays) return 'critical';
  if (days <= thresholds.highDays)     return 'high';
  if (days <= thresholds.mediumDays)   return 'medium';
  return 'low';
}
